import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Button, DialogActions } from '@mui/material';
import { Close, ArrowBack, ArrowForward, Autorenew } from '@mui/icons-material';
import Score from '../core/ScoreStars';
import ConfettiExplosion from 'react-confetti-explosion';
import { useStoreByName } from '../../../hooks/use-stores.hooks';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { getRandomFeedback } from '../../../utils/display.utils';
import { getRandomSound, preloadAllSounds, getPreloadedAudio, playFlowChime } from '../../../utils/sound.utils';
import { adjustScale } from '../../../utils/display.utils';
import { LoaderComponent } from '../core/LoaderComponent';  
import TextElement from '../text/TextElement';
/** מצב הזרימה הרציפה, כפי שהוא מגיע מ-ReadingModeContainer */
interface AutoFlowState {
  /** האם להמשיך לבד בסיום הפסוק */
  active: boolean;
  /** ההמשך עצמו - חזרה על הפסוק או מעבר לבא */
  onContinueNow: () => void;
}

interface ScoreAndResultDialogProps {
  score: number;
  isOpen: boolean;
  onClose: () => void;
  /** "שנן שוב" - באותו פסוק */
  onReadAgain?: () => void;
  /** "הקטע הבא" */
  onNextPassage?: () => void;
  autoFlow?: AutoFlowState;
}

const ScoreAndResultDialog: React.FC<ScoreAndResultDialogProps> = ({
  score,
  isOpen,
  onClose,
  onReadAgain,
  onNextPassage,
  autoFlow,
}) => {
  const [confetti, setConfetti] = useState(false);
  const [feedback, setFeedback] = useState<[string, string?]>(['', '']);
  const [loading, setLoading] = useState(false);
  const [fontSize, setFontSize] = useState('32px');

  const isAutoFlowActive = Boolean(autoFlow?.active);

  // שומרים את הפעולה בref כדי שהטיימר לא יתאפס בכל רינדור מחדש של ההורה
  const continueNowRef = useRef<() => void>(() => undefined);
  continueNowRef.current = autoFlow?.onContinueNow ?? (() => undefined);

  const { goNextOrPrevEntity } = useStoreByName('booksStore');
  const { selectedBook, selectedTextEntity } = useStoreByName('booksStore');
  const { userTextToDisplay } = useStoreByName('speechRecognitionStore');
  const { t, i18n } = useTranslation();
  const { calculateAndSendRank } = useStoreByName('speechRecognitionStore');

  // פונקציה לחישוב גודל פונט בהתאם לאורך התוכן
  const calculateFontSize = (text: string) => {
    const totalLength = text.length;
    const isMobile = window.innerWidth <= 768;
    
    if (isMobile) {
      if (totalLength <= 10) return '24px';
      if (totalLength <= 20) return '20px';
      if (totalLength <= 30) return '18px';
      return '16px';
    } else {
      if (totalLength <= 10) return '40px';
      if (totalLength <= 20) return '32px';
      if (totalLength <= 30) return '28px';
      if (totalLength <= 40) return '24px';
      return '20px';
    }
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setFeedback(getRandomFeedback(score, i18n.language));

    // חישוב דרגה - לא חוסם, רץ ברקע
    calculateAndSendRank().catch((error: unknown) => {
      console.error('שגיאה בחישוב דרגה:', error);
    });

    // בזרימה רציפה: צליל קצר בלבד. מי שביקש רצף ביקש קצב, לא טקס ניצחון
    // אחרי כל פסוק - בלי קונפטי, בלי מנגינה ארוכה, בלי מסך ביניים ובלי שורת כפתורים.
    if (isAutoFlowActive) {
      playFlowChime(score);
      return;
    }

    setConfetti(true);

    // השמעת סאונד ברקע - לא חוסם את האפליקציה
    let currentSound: HTMLAudioElement | null = null;
    
    try {
      const sound = getPreloadedAudio(score);
      sound.playbackRate = 1.5;
      currentSound = sound;

      // בדיקה בסיסית - אם אין src, לא ננסה לנגן
      if (sound.src && sound.src !== 'undefined' && sound.src !== '') {
        // ניסיון לנגן - לא מחכים לתוצאה
        sound.play().catch((error: unknown) => {
          // רק לוגים - לא מעכב את האפליקציה
          console.warn('שגיאה בניגון קובץ השמע (לא קריטי):', error);
        });
      }
    } catch (error) {
      // שגיאה ביצירת אובייקט השמע - לא קריטי
      console.warn('שגיאה בהכנת השמע (לא קריטי):', error);
    }

    // Cleanup - עצירת הסאונד אם הקומפוננטה unmount או אם isOpen משתנה
    return () => {
      setConfetti(false);
      if (currentSound) {
        try {
          currentSound.pause();
          currentSound.currentTime = 0;
        } catch (error) {
          // שקט על שגיאות cleanup
        }
      }
    };
  }, [isOpen, score, calculateAndSendRank, i18n.language, isAutoFlowActive]);

  // בזרימה רציפה ממשיכים מיד: הצליל מתנגן ואנחנו כבר בפסוק הבא (או בחזרה עליו).
  // אפס המתנה - הטיימר הוא רק כדי לצאת ממחזור הרינדור הנוכחי.
  useEffect(() => {
    if (!isOpen || !isAutoFlowActive) return;

    const timeout = setTimeout(() => continueNowRef.current(), 0);
    return () => clearTimeout(timeout);
  }, [isOpen, isAutoFlowActive]);

  useEffect(() => {
    if (feedback[0]) {
      const fullText = feedback[0] + (feedback[1] ? ` (${feedback[1]})` : '');
      setFontSize(calculateFontSize(fullText));
    }
  }, [feedback]);

  return (
    <>
      {loading && <LoaderComponent />}
      {confetti && isOpen && !isAutoFlowActive && score >= 80 && (
        <ConfettiExplosion
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
          height={'100vh'}
          width={window.innerWidth}
          particleCount={1000}
          duration={3000}
          zIndex={9999}
          colors={['#c0c0c0', '#d4af37', '#ffd700', '#e5e4e2', '#b8860b', '#daa520']}
        />
      )}

      {!loading && !isAutoFlowActive && feedback[0] && (
        <Dialog onClose={onClose} aria-labelledby="customized-dialog-title" open={isOpen} className="score-modal">
                      <DialogTitle className="animated-text" style={{ fontSize }}>
              {feedback[0]} {feedback[1] && <span style={{ display: i18n.language === 'en' ? 'block' : 'inline' }}>({feedback[1]})</span>}
              <IconButton aria-label="close" onClick={onClose} className="close-button">
                <Close />
              </IconButton>
            </DialogTitle>
          <Score value={score} strokeColor="black" />
          <DialogContent dividers>
            <div className="reading-result">
              <TextElement
                mode="result"
                fontClassName={
                  selectedBook?.name_en?.includes('Siddur') ? 'Drogowskaz' :
                  selectedBook?.name_en === 'Mishna' ? 'OpenFrank' : 'Ezra'
                }
                wordStatuses={userTextToDisplay}
                showArrowIndicator={false}
                preventCopyPaste={false}
              />
            </div>
          </DialogContent>
          <DialogActions sx={{ padding: '8px', flexWrap: 'wrap' }}>
            <Button
              variant="text"
              onClick={() => {
                if (onReadAgain) {
                  onReadAgain();
                  return;
                }
                onClose();
              }}
              startIcon={<Autorenew />}
            >
              {t('readAgain')}
            </Button>
            <Button
              variant="text"
              onClick={() => {
                if (onNextPassage) {
                  onNextPassage();
                  return;
                }
                goNextOrPrevEntity(true);
                onClose();
              }}
              endIcon={i18n.dir() === 'ltr' ? <ArrowForward /> : <ArrowBack />}
            >
              {t('nextPassage')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default observer(ScoreAndResultDialog);
